import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';
import { CONVERSATION_STATUS_LABELS } from '../i18n/conversation-labels';
import type { ConversationScope, ConversationView } from '../models/conversation-view.types';
import { computeSlaBadge } from '../sla/sla-badge';
import { createNowSignal } from '../sla/now-signal';
import type { ConversationServerFilters } from './conversations-api.service';
import { ConversationsStore } from './conversations.store';

const CONTEXT_PANEL_STORAGE_KEY = 'automato-conversations-context-panel-open';
const FILTERS_PINNED_STORAGE_KEY = 'automato-conversations-filters-pinned';

export interface SituationFilter {
  id: string;
  label: string;
  count: number;
  tone: 'neutral' | 'warning' | 'danger' | 'success';
}

export interface TeamFilter {
  id: string;
  label: string;
  count: number;
}

export interface ConversationTag {
  id: string;
  label: string;
  count: number;
}

/** Tabs shown in the conversation details panel. */
export type DetailsTab = 'details' | 'notes' | 'history';
export type ConversationRoutePreset = 'overview' | 'mine' | 'waiting';

const SITUATION_DEFINITIONS: readonly Omit<SituationFilter, 'count'>[] = [
  { id: 'unassigned', label: 'Sem responsável', tone: 'warning' },
  { id: 'sla-risk', label: 'SLA próximo', tone: 'danger' },
  { id: 'ai-active', label: 'Com agente IA', tone: 'success' },
  { id: 'paused', label: 'Pausadas', tone: 'neutral' },
];

/**
 * View-state for the inbox filter rail: scope/team/situation/tags/search plus the rail and
 * details-panel layout toggles (persisted to localStorage). Derives the filtered conversation
 * list and filter counters from {@link ConversationsStore}. Provided at the feature level.
 */
@Injectable()
export class ConversationFiltersStore {
  private readonly conversationsStore = inject(ConversationsStore);
  private readonly document = inject(DOCUMENT);
  /** Relógio reativo do filtro "risco de SLA" — sem ele o predicado congelava com a tela aberta. */
  private readonly now = createNowSignal();

  readonly activeScope = signal<ConversationScope>('all');
  readonly activeTeam = signal('todas');
  readonly selectedSituation = signal('todas');
  readonly selectedTags = signal<readonly string[]>([]);
  readonly selectedStatus = signal('todas');
  readonly selectedPriority = signal('todas');
  readonly filterUnread = signal(false);
  readonly searchQuery = signal('');
  readonly filtersPinned = signal(this.readStoredBoolean(FILTERS_PINNED_STORAGE_KEY));
  readonly filtersPreviewOpen = signal(false);
  readonly filterMenuOpen = signal(false);
  readonly detailsOpen = signal(this.readStoredBoolean(CONTEXT_PANEL_STORAGE_KEY));
  /** Active tab of the details panel, shared so the thread can jump to the notes tab. */
  readonly detailsTab = signal<DetailsTab>('details');
  /** Note the user navigated to from the thread marker, to highlight in the notes list. */
  readonly focusedNoteId = signal<string | null>(null);

  readonly activeFilterCount = computed(
    () =>
      (this.activeScope() === 'mine' ? 1 : 0) +
      (this.activeTeam() !== 'todas' ? 1 : 0) +
      (this.selectedSituation() !== 'todas' ? 1 : 0) +
      (this.selectedStatus() !== 'todas' ? 1 : 0) +
      (this.selectedPriority() !== 'todas' ? 1 : 0) +
      (this.filterUnread() ? 1 : 0) +
      this.selectedTags().length,
  );

  readonly filtersExpanded = computed(
    () => this.filtersPinned() || this.filtersPreviewOpen() || this.filterMenuOpen(),
  );

  readonly tagFilters = computed<readonly ConversationTag[]>(() => {
    const tagCounts = new Map<string, { label: string; count: number }>();

    for (const conversation of this.conversationsStore.conversations()) {
      for (const label of conversation.labels) {
        const id = label.id;
        const current = tagCounts.get(id);
        tagCounts.set(id, { label: label.name, count: (current?.count ?? 0) + 1 });
      }
    }

    return Array.from(tagCounts.entries())
      .map(([id, tag]) => ({ id, label: tag.label, count: tag.count }))
      .sort((left, right) => left.label.localeCompare(right.label, 'pt-BR'));
  });

  readonly situationFilters = computed<readonly SituationFilter[]>(() =>
    SITUATION_DEFINITIONS.map((situation) => ({
      ...situation,
      count: this.conversationsStore
        .conversations()
        .filter((conversation) => this.matchesSituation(situation.id, conversation)).length,
    })),
  );

  readonly teamFilters = computed<readonly TeamFilter[]>(() => {
    const conversations = this.conversationsStore.conversations();
    const teamCounts = new Map<string, number>();

    for (const conversation of conversations) {
      if (conversation.team !== 'Sem equipe') {
        teamCounts.set(conversation.team, (teamCounts.get(conversation.team) ?? 0) + 1);
      }
    }

    return [
      { id: 'todas', label: 'Todas as equipes', count: conversations.length },
      ...Array.from(teamCounts.entries()).map(([team, count]) => ({ id: team, label: team, count })),
    ];
  });

  readonly selectedTeamLabel = computed(
    () => this.teamFilters().find((team) => team.id === this.activeTeam())?.label ?? 'Todas as equipes',
  );

  readonly selectedSituationLabel = computed(
    () =>
      this.situationFilters().find((situation) => situation.id === this.selectedSituation())?.label ??
      'Todas as situações',
  );

  readonly selectedStatusLabel = computed(() => STATUS_LABELS[this.selectedStatus()] ?? 'Todos os status');
  readonly selectedPriorityLabel = computed(() => PRIORITY_LABELS[this.selectedPriority()] ?? 'Todas as prioridades');

  readonly conversations = computed<readonly ConversationView[]>(() => {
    const activeScope = this.activeScope();
    const activeTeam = this.activeTeam();
    const selectedSituation = this.selectedSituation();
    const selectedTags = this.selectedTags();
    const searchQuery = this.searchQuery();

    return this.conversationsStore.conversations().filter((conversation) => {
      const scopeMatch = activeScope === 'all' || conversation.assignedToMe;
      const teamMatch = activeTeam === 'todas' || conversation.team === activeTeam;
      const tagMatch =
        selectedTags.length === 0 ||
        selectedTags.every((selectedTag) => conversation.labels.some((label) => label.id === selectedTag));
      const situationMatch = selectedSituation === 'todas' || this.matchesSituation(selectedSituation, conversation);
      const searchMatch =
        searchQuery.length === 0 ||
        normalizeSearch(
          [
            conversation.contact,
            conversation.company,
            conversation.channel,
            conversation.channelIdentity,
            conversation.owner,
            CONVERSATION_STATUS_LABELS[conversation.status],
            conversation.priority,
            conversation.lastMessage,
            ...conversation.labels.map((label) => label.name),
          ].join(' '),
        ).includes(searchQuery);

      return scopeMatch && teamMatch && situationMatch && tagMatch && searchMatch;
    });
  });

  setActiveScope(scope: ConversationScope): void {
    this.activeScope.set(scope);
  }

  setActiveTeam(teamId: string): void {
    this.activeTeam.set(teamId);
  }

  setSituation(situationId: string): void {
    this.selectedSituation.set(situationId);
  }

  toggleTag(tagId: string): void {
    this.selectedTags.update((selectedTags) =>
      selectedTags.includes(tagId) ? selectedTags.filter((tag) => tag !== tagId) : [...selectedTags, tagId],
    );
  }

  isTagSelected(tagId: string): boolean {
    return this.selectedTags().includes(tagId);
  }

  setStatus(status: string): void {
    this.selectedStatus.set(status);
    this.triggerServerReload();
  }

  setPriority(priority: string): void {
    this.selectedPriority.set(priority);
    this.triggerServerReload();
  }

  toggleFilterUnread(): void {
    this.filterUnread.update((v) => !v);
    this.triggerServerReload();
  }

  applySearch(value: string): void {
    this.searchQuery.set(normalizeSearch(value));
  }

  applyRoutePreset(preset: ConversationRoutePreset): void {
    const nextScope: ConversationScope = preset === 'mine' ? 'mine' : 'all';
    const nextStatus = preset === 'waiting' ? 'pending' : 'todas';

    this.activeScope.set(nextScope);
    this.activeTeam.set('todas');
    this.selectedSituation.set('todas');
    this.selectedTags.set([]);
    this.selectedStatus.set(nextStatus);
    this.selectedPriority.set('todas');
    this.filterUnread.set(false);

    // Always reload with explicit server filters: the singleton ConversationsStore keeps the
    // last-used serverFilters sticky (for realtime reloads), and this store is recreated per
    // page — comparing against our own fresh defaults says nothing about what the singleton
    // last sent. Without the explicit reset, the previous tab's filter (e.g. status=pending
    // from "Aguardando") leaks into this tab and can render an empty inbox.
    this.triggerServerReload();
  }

  private triggerServerReload(): void {
    const serverFilters: ConversationServerFilters = {};
    const status = this.selectedStatus();
    if (status !== 'todas') serverFilters.status = status;
    const priority = this.selectedPriority();
    if (priority !== 'todas') serverFilters.priority = priority;
    if (this.filterUnread()) serverFilters.hasUnread = true;
    this.conversationsStore.loadConversations({ preserveSelection: true, serverFilters });
  }

  openFiltersPreview(): void {
    this.filtersPreviewOpen.set(true);
  }

  closeFiltersPreview(): void {
    this.filtersPreviewOpen.set(false);
  }

  setFilterMenuOpen(isOpen: boolean): void {
    this.filterMenuOpen.set(isOpen);
  }

  toggleFiltersPinned(): void {
    this.filtersPinned.update((isPinned) => {
      const nextPinned = !isPinned;
      if (nextPinned) {
        this.closeDetailsPanel();
      }
      this.persist(FILTERS_PINNED_STORAGE_KEY, nextPinned);
      return nextPinned;
    });
  }

  setDetailsTab(tab: DetailsTab): void {
    this.detailsTab.set(tab);
    if (tab !== 'notes') {
      this.focusedNoteId.set(null);
    }
  }

  /** Open the details panel on the notes tab and highlight the given note (from a thread marker). */
  focusNote(noteId: string): void {
    this.openDetailsPanel();
    this.detailsTab.set('notes');
    this.focusedNoteId.set(noteId);
  }

  openDetailsPanel(): void {
    if (this.detailsOpen()) {
      return;
    }
    this.closePinnedFilters();
    this.detailsOpen.set(true);
    this.persist(CONTEXT_PANEL_STORAGE_KEY, true);
  }

  toggleDetailsPanel(): void {
    this.detailsOpen.update((isOpen) => {
      const nextOpen = !isOpen;
      if (nextOpen) {
        this.closePinnedFilters();
      }
      this.persist(CONTEXT_PANEL_STORAGE_KEY, nextOpen);
      return nextOpen;
    });
  }

  closeDetailsPanel(): void {
    if (!this.detailsOpen()) {
      return;
    }
    this.detailsOpen.set(false);
    this.persist(CONTEXT_PANEL_STORAGE_KEY, false);
  }

  private closePinnedFilters(): void {
    if (!this.filtersPinned()) {
      return;
    }
    this.filtersPinned.set(false);
    this.persist(FILTERS_PINNED_STORAGE_KEY, false);
  }

  private persist(key: string, value: boolean): void {
    this.document.defaultView?.localStorage.setItem(key, String(value));
  }

  private readStoredBoolean(key: string): boolean {
    return this.document.defaultView?.localStorage.getItem(key) === 'true';
  }

  /** SLA-risk uses the real SLA computation; other situations are pure predicates on the view. */
  private matchesSituation(situationId: string, conversation: ConversationView): boolean {
    if (situationId === 'sla-risk') {
      const badge = computeSlaBadge(
        {
            firstResponseAt: conversation.firstResponseAt,
          status: conversation.status,
          awaitingReplySince: conversation.awaitingReplySince ?? null,
          awaitingTrack: conversation.awaitingTrack ?? null,
        },
        this.conversationsStore.slaPolicy(),
        // Mesmo relógio reativo do selo: sem ele o filtro "risco de SLA" não detectava conversas que
        // estouram enquanto a tela está aberta.
        this.now(),
      );
      return badge?.tone === 'warning' || badge?.tone === 'danger';
    }
    return matchesSituationFilter(situationId, conversation);
  }
}

const STATUS_LABELS: Record<string, string> = {
  todas: 'Todos os status',
  open: 'Abertas',
  pending: 'Pendentes',
  agent: 'Com agente',
  snoozed: 'Pausadas',
  resolved: 'Resolvidas',
  archived: 'Arquivadas',
};

const PRIORITY_LABELS: Record<string, string> = {
  todas: 'Todas as prioridades',
  low: 'Baixa',
  normal: 'Normal',
  high: 'Alta',
  urgent: 'Urgente',
};

function matchesSituationFilter(situationId: string, conversation: ConversationView): boolean {
  switch (situationId) {
    case 'unassigned':
      return conversation.owner === 'Fila';
    case 'ai-active':
      return conversation.status === 'agent' || conversation.owner.startsWith('Agente');
    case 'paused':
      return conversation.status === 'snoozed';
    default:
      return false;
  }
}

function normalizeSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .trim();
}
